// T271(2) — the one list of authenticated routes.
//
// The sidebar and the command palette each kept their own, and the drift showed
// up twice: T183 found Analytics and Sources missing from the palette, and the
// /digest route added afterwards repeated the same regression immediately. Two
// hand-maintained lists of the same thing will always diverge at the moment
// somebody adds a page; one list cannot.
//
// `badgeKey` is the sidebar's business and `label` is shared. The palette
// prefixes "Go to " itself — its rows read as commands, the sidebar's as places.
// T433 — one entry per route, and every consumer derives from it.
//
// `title` is the browser tab and `badgeNoun` is what the badge counts, both
// added here rather than kept in the files that render them. Layout.jsx had
// its own hand-written TITLES map and it was missing `/tasks` and `/mail` —
// so the two pages he opens most (Tasks had 26 visits the day this was
// written, more than any page but the dashboard) both read a bare "hunter"
// in the tab strip and in the screen-reader route announcement. That is
// precisely the ten-identical-tabs problem T250 was written to end, found
// again eleven months later because the fix lived in a second list.
//
// `badgeNoun` replaces a function in Sidebar.jsx whose else-branch called
// EVERY badge "applications still in the pipeline" — true for Applications,
// a lie for Tasks and Mail, and the kind of lie that only a screen-reader
// user or a hover hears.
export const ROUTES = [
  { to: '/', label: 'Dashboard', title: 'Dashboard', icon: '◉', end: true, badgeKey: 'newCount', badgeNoun: ['new recommendation', 'new recommendations'] },
  { to: '/digest', label: 'Digest', title: 'Digest', icon: '✉', badgeKey: 'unreadDigests', badgeNoun: ['unread digest', 'unread digests'] },
  // T433 — ☰ was the hamburger AND the Applications glyph, in a product
  // where the hamburger sits at the top of the same screen on mobile.
  { to: '/applications', label: 'Applications', title: 'Applications', icon: '▦', badgeKey: 'inFlightCount', badgeNoun: ['application in the pipeline', 'applications in the pipeline'] },
  // T360 — replies the matcher would not place on its own. Badged, because a
  // queue nobody is told about is a queue nobody clears.
  // T376 — everything waiting on him, in one place.
  { to: '/tasks', label: 'Tasks', title: 'Tasks', icon: '✓', badgeKey: 'openTasks', badgeNoun: ['thing waiting on you', 'things waiting on you'] },
  { to: '/mail', label: 'Mail', title: 'Unlinked mail', icon: '✎', badgeKey: 'unlinkedMail', badgeNoun: ['message to place', 'messages to place'] },
  { to: '/analytics', label: 'Analytics', title: 'Analytics', icon: '▤' },
  { to: '/sources', label: 'Sources', title: 'Sources', icon: '⇄' },
  { to: '/profile', label: 'Profile', title: 'Profile', icon: '✦' },
  { to: '/documents', label: 'Documents', title: 'Documents', icon: '⛁' },
  { to: '/settings', label: 'Settings', title: 'Settings', icon: '⚙' },
];

/** T433 — the browser tab, derived. One list, so a new page cannot forget. */
export const TITLE_BY_PATH = Object.fromEntries(ROUTES.map((r) => [r.to, r.title]));

/** T433 — what a badge is counting, in words, singular or plural. */
export function badgeLabel(to, count) {
  const noun = ROUTES.find((r) => r.to === to)?.badgeNoun;
  if (!noun) return `${count}`;
  return `${count} ${count === 1 ? noun[0] : noun[1]}`;
}

/** Palette rows, derived — never hand-listed again. */
export const navCommands = () =>
  ROUTES.map((r) => ({
    id: `go-${r.label.toLowerCase()}`,
    label: `Go to ${r.label}`,
    section: 'Navigate',
    kind: 'nav',
    to: r.to,
  }));
